'use client';

import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { PropsWithChildren, createContext } from 'react';

import { getTranslatedProfileApi, getUserProfileApi, getUserProfileByProfileIdApi } from '@flipster/apis/v1/user';
import { GetTranslatedProfileResponse } from '@flipster/apis/v1/user/types';
import { overlayBunja } from '@flipster/overlay';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode } from '@flipster/utils/error';

import { authBunja } from '../shared/auth/state';
import { followBunja } from '../shared/community/follow';
import { profileSettingBunja } from '../shared/community/profile';
import { EditProfileModal } from './modal/edit-profile';
import { oauthProviderSearchParamKey } from './modal/edit-profile/shared/oauth';

export const UserProfileQueryKey = ['user', 'profile'];
export const UserFollowQueryKey = ['user', 'follow'];
export function getUserProfileQueryKey(profileId: string | undefined, isLoggedIn: boolean) {
  return [...UserProfileQueryKey, profileId, isLoggedIn];
}
export const ProfileContext = createContext<string | undefined>(undefined);
export const ProfileScope = createScopeFromContext(ProfileContext);
export const profileBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const overlay = bunja.use(overlayBunja);
  const profileId = bunja.use(ProfileScope);
  const { isLoggedInAtom, userAtom } = bunja.use(authBunja);
  const { putUserProfileMutationAtom } = bunja.use(profileSettingBunja);
  const { postUserFollowMutationAtom, deleteUserFollowMutationAtom } = bunja.use(followBunja);

  const baseProfileAtom = atomWithQuery((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    // if profileScope is empty, load my profile
    if (profileId == null) {
      return {
        queryKey: getUserProfileQueryKey(profileId, isLoggedIn),
        queryFn: getUserProfileApi,
        enabled: isLoggedIn,
        experimental_prefetchInRender: true,
      };
    }
    return {
      queryKey: getUserProfileQueryKey(profileId, isLoggedIn),
      queryFn: () => getUserProfileByProfileIdApi(profileId),
    };
  });
  const isLoadingAtom = atom((get) => get(baseProfileAtom).isLoading);
  const profileAtom = atom((get) => get(baseProfileAtom).data?.data);
  const profileSettingsAtom = atom((get) => get(profileAtom)?.settings);
  const isMyProfileAtom = atom((get) => profileId != null || get(profileAtom)?.isMyProfile);
  const notExistProfileAtom = atom((get) => {
    const { error } = get(baseProfileAtom);
    return getErrorCode(error) === 'api.user.profile.notExist';
  });
  const myProfileIdAtom = atom((get) => get(userAtom)?.profileId ?? '');
  const resolvedProfileIdAtom = atom((get) => profileId ?? get(myProfileIdAtom));
  const masterProfileAtom = atom((get) => {
    return get(profileAtom)?.copyTradeMasterProfile;
  });

  async function refetchProfile() {
    const { refetch } = store.get(baseProfileAtom);
    await refetch();
  }

  const translatedProfileAtom = atom<GetTranslatedProfileResponse | undefined>(undefined);
  const translationMutationAtom = atomWithMutation(() => ({
    mutationFn: () => getTranslatedProfileApi(profileId!, { lang: navigator.language }),
    enabled: profileId != null,
  }));
  const isTranslationLoadingAtom = atom((get) => get(translationMutationAtom).isPending);
  async function fetchTranslation() {
    try {
      if (!store.get(translatedProfileAtom)) {
        const result = await store.get(translationMutationAtom).mutateAsync();
        store.set(translatedProfileAtom, result.data);
      }
    } catch {
      /** empty block */
    }
  }

  // for processing oauth callback we should open modal first
  // see ./edit-profile-modal/bunja.ts
  bunja.effect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(oauthProviderSearchParamKey)) {
      const { promise } = store.get(baseProfileAtom);
      promise.then(() => overlay.open(() => <EditProfileModal />));
    }
  });

  return {
    profileId,
    isLoadingAtom,
    profileAtom,
    masterProfileAtom,
    translatedProfileAtom,
    isTranslationLoadingAtom,
    isMyProfileAtom,
    myProfileIdAtom,
    resolvedProfileIdAtom,
    profileSettingsAtom,
    notExistProfileAtom,
    putUserProfileMutationAtom,
    postUserFollowMutationAtom,
    deleteUserFollowMutationAtom,
    // fns
    refetchProfile,
    fetchTranslation,
  };
});
profileBunja.debugLabel = 'profileBunja';

// https://github.com/vercel/next.js/issues/75992
export const ProfileContextProvider = ({ children, value }: PropsWithChildren<{ value: string }>) => {
  return <ProfileContext value={value}>{children}</ProfileContext>;
};
